/**
 * @file master-products.tsx
 * @module app/modules/uProcure/products/master-products
 * @description Implements the master products interface for UProcure operations.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import { useEffect, useState } from "react";
import { Button, Card, Col, Row } from "react-bootstrap";
import { KTSVG } from "../../../../_admin/helpers";
import { CustomSelectWhite } from "../../../custom/Select/CustomSelectWhite";
import { CustomComponentSelect } from "../../../custom/Select/CustomComponentSelect";
import gray from "../../../../_admin/assets/media/svg_uTrade/gray_dot.svg";
import green from "../../../../_admin/assets/media/svg_uTrade/green_dot.svg";
import DraftProducts from "../../../modals/draft-products";
import APICallService from "../../../../api/apiCallService";
import { master, product } from "../../../../api/apiEndPoints";
import { productJSON } from "../../../../api/apiJSON/product";
import Loader from "../../../../Global/loader";
import { useNavigate } from "react-router-dom";
import Pagination from "../../../../Global/pagination";
import {
  Actived,
  Add,
  AllProduct,
  Deactivated,
  Delete,
  Edit,
  MasterProduct,
  PAGE_LIMIT,
  View,
} from "../../../../utils/constants";
import ThreeDotMenu from "../../../../_admin/assets/media/svg_uTrade/three-dot.svg";
import { CustomSelectTable } from "../../../custom/Select/CustomSelectTable";
import {
  listMasterProductDetails,
  listMasterProducts,
} from "../../../../utils/storeString";
import { getKey, removeKey, setKey } from "../../../../Global/history";
import { ElementAnimateUtil } from "../../../../_admin/assets/ts/_utils";
import { catalogue, masterToast } from "../../../../utils/toast";
import { success, error } from "../../../../Global/toast";
import DeleteModal from "../../../modals/delete";
import Method from "../../../../utils/methods";
import { useAuth } from "../../auth";
// import { warehouse as warehouseStoreString } from "../../../utils/storeString";
import EditSearchTags from "../../../modals/searchTagModal";
import { useWarehouse } from "../../../context/WarehouseContext";
import { useDebounce } from "../../../../utils/useDebounce";
import ImageWithFallback from "../../_common/components/image-with-fallback";
const MasterProducts = () => {

  const { selectedWarehouse } = useWarehouse();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const productOptions = [
    {
      value: AllProduct,
      name: "All products",
      label: (
        <>
          <span className="fs-16 fw-600 text-black mb-0">All products</span>
        </>
      ),
      title: "All products",
    },
    ,
    {
      value: Actived,
      name: "Active",
      label: (
        <>
          <img src={green} width={12} height={12} className="me-3" alt="" />
          <span className="fs-16 fw-600 text-black ">Active</span>
        </>
      ),
      title: "Active",
    },
    {
      value: Deactivated,
      name: "Deactivated",
      label: (
        <>
          <img src={gray} width={12} height={12} className="me-3" alt="" />
          <span className="fs-16 fw-600 text-black">Deactivated</span>
        </>
      ),
      title: "Deactivated",
    },
  ];
  const [filterProducts, setFilterProducts] = useState<any>([]);
  const [products, setProducts] = useState<any>([]);
  const [draftCount, setDraftCount] = useState<number>(0);
  const [fetchLoader, setFetchLoader] = useState(false);
  const [fetchCategoryLoader, setFetchCategoryLoader] = useState(false);
  const [dataFetched, setDataFetched] = useState(false);

  const [showDraftProducts, setShowDraftProducts] = useState(false);
  const [showRemoveDraftProduct, setShowRemoveDraftProduct] = useState(false);
  const [id, setId] = useState("");
  const [title, setTitle] = useState("");
  const [totalRecords, setTotalRecords] = useState(0);
  const [productState, setProductState] = useState<any>(
    getKey(listMasterProducts.filterProductState) || AllProduct
  );
  const [page, setPage] = useState<any>(getKey(listMasterProducts.page) || 1);
  const [pageLimit, setPageLimit] = useState<any>(
    getKey(listMasterProducts.limit) || PAGE_LIMIT
  );
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<any>(
    getKey(listMasterProducts.filterCategories) || []
  );
  const [totalCategory, setTotalCategory] = useState<number>(0);
  const [categoryPage, setCategoryPage] = useState(1);
  const [categorySearch, setCategorySearch] = useState("");
  const [search, setSearch] = useState<string>(
    getKey(listMasterProducts.search)?.toString() || ""
  );
  const [show, setShow] = useState(false);
  const [defaultValue, setDefaultValue] = useState<any>();
  const [deleteProductModalOpen, setDeleteProductModalOpen] = useState(false);
  const [error, setError] = useState("");
  const [selectedProduct, setSelectedProduct] = useState<any>();
  const [showSearchTag, setSearchTag] = useState(false)
  const [deleteVariantModalOpen, setDeleteVariantModalOpen] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState<any>();
  const [productId, setProductId] = useState("");
  const [deleteLoader, setDeleteLoader] = useState(false);

  useEffect(() => {
    (async () => {
      if (!Method.hasModulePermission(MasterProduct, currentUser)) {
        return window.history.back();
      }
      setFetchLoader(true);
      removeKey(listMasterProductDetails.currentTab);
      await fetchProducts(page, pageLimit, productState, categories, search);
      setDataFetched(true);
      setFetchLoader(false);
      await fetchDraftCount();
      await fetchCategory(categoryPage, PAGE_LIMIT);
      setDefaultValue({
        label: (
          <img
            className="img-fluid"
            width={24}
            height={5}
            src={ThreeDotMenu}
            alt=""
          />
        ),
        value: (
          <img
            className="img-fluid"
            width={24}
            height={5}
            src={ThreeDotMenu}
            alt=""
          />
        ),
      });
    })();
  }, []);

  useEffect(() => {
    if (dataFetched) {
      let initialScrollPosition: any = getKey(
        listMasterProducts.scrollPosition
      );
      if (initialScrollPosition) {
        ElementAnimateUtil.scrollTo(parseInt(initialScrollPosition));
        setDataFetched(false);
      }
      setKey(listMasterProducts.search, "", false);
      setKey(listMasterProducts.filterProductState, AllProduct, false);
      setKey(listMasterProducts.filterCategories, [], false);
    }
  }, [dataFetched]);

  const fetchCategory = async (
    page: number,
    limit: number,
    search: string = ""
  ) => {
    setFetchCategoryLoader(true);
    let params: any = {
      pageNo: page,
      limit: limit,
      searchTerm: search,
      sortKey: "title",
      sortOrder: 1,
      needCount: true,
      state: 1,
      categoriesDepth: 1,
    };
    if (selectedWarehouse?.value) {
      params.warehouse = selectedWarehouse?.value;
    }
    let apiService = new APICallService(
      master.categoryList,
      params,
      "",
      "",
      "",
      "",
      MasterProduct,
      selectedWarehouse?.value
    );
    let response = await apiService.callAPI();
    if (response.records) {
      response.records = await response.records.map((val: any) => {
        return {
          value: val.title,
          name: val.title,
          id: val._id,
          label: (
            <>
              <div className="symbol symbol-40px symbol-circle border me-2">
                <ImageWithFallback src={val.image} className="object-fit-contain" alt="" noDefaultSize />
              </div>
              <label
                className="form-check-label fs-16 fw-600 text-dark"
                htmlFor="Ex2"
              >
                {val.title}
              </label>
            </>
          ),
          title: val.title,
        };
      });
      setTotalCategory(response.total);
      let data: any = (prevResults: any) =>
        page === 1 ? response.records : [...prevResults, ...response.records];
      setFilterProducts(data);
    }
    setFetchCategoryLoader(false);
  };
  const onMenuCategoryScrollToBottom = async () => {
    if (!(filterProducts.length === totalCategory)) {
      let tempPage = categoryPage;
      tempPage = tempPage + 1;
      setCategoryPage(tempPage);
      await fetchCategory(tempPage, PAGE_LIMIT, categorySearch);
    }
  };
  const onMenuCategoryClose = async () => {
    setCategorySearch("");
  };
  const onMenuCategoryOpen = async () => {
    setCategoryPage(1);
    await fetchCategory(1, PAGE_LIMIT, categorySearch);
  };
  const handleInputCategoryChange = async (newValue: string) => {
    setCategoryPage(1);
    setCategorySearch(newValue);
    await fetchCategory(1, 0, newValue);
  };
  const fetchProducts = async (
    pageNo: number,
    limit: number,
    state: number,
    categories: any,
    search: string
  ) => {
    setLoading(true);
    let data: any = {
      pageNo: pageNo,
      limit: limit,
      sortKey: "_createdAt",
      sortOrder: -1,
      searchTerm: search,
      state: state,
      needCount: true,
    };
    if (categories.length > 0) {
      categories.map((val: any, index: number) => {
        data = { ...data, ["categories[" + index + "]"]: val.id };
      });
    }
    if (selectedWarehouse?.value) {
      data.warehouse = selectedWarehouse?.value;
    }
    let apiService = new APICallService(
      product.listProduct,
      data,
      "",
      "",
      "",
      "",
      MasterProduct,
      selectedWarehouse?.value
    );
    let response = await apiService.callAPI();
    if (response.records) {
      if (response.total) {
        setTotalRecords(response.total);
      }
      setProducts(response.records);
    } else {
      setProducts([]);
    }
    setLoading(false);
  };
  const fetchDraftCount = async () => {
    let apiService = new APICallService(
      product.draftCount,
      "",
      "",
      "",
      "",
      "",
      MasterProduct,
      selectedWarehouse?.value
    );
    let response = await apiService.callAPI();
    if (response.count) {
      setDraftCount(response.count);
    }
  };
  const handleVariationShow = (variantionData: any, index: number) => {
    let temp: any = [...products];
    temp[index].showVariant = !temp[index].showVariant;
    setProducts(temp);
  };
  const handleShowRemoveDraftProduct = (id: string, title: string) => {
    setShowDraftProducts(false);
    setId(id);
    setTitle(title);
    setShowRemoveDraftProduct(true);
  };
  const handleHideRemoveDraftProduct = () => {
    setShowRemoveDraftProduct(false);
    setId("");
    setTitle("");
  };
  const handleDraftCount = () => {
    let temp = draftCount;
    temp = temp - 1;
    setDraftCount(temp);
  };
  const updateProductState = async (
    state: boolean,
    id: string,
    isProduct: boolean,
    productIndex: number,
    variantIndex: number
  ) => {
    console.log("id", id);
    let temp = [...products];
    let processedData: { productName: string }[] = [];

    temp.forEach((product) => {
      product.variants.forEach((variant: any) => {
        if (variant.variant._id === id) {
          processedData.push({
            productName: `(${variant.variant.title}) product variant active status has been changed`,
          });
        }
      });
    });
    let apiService = new APICallService(
      product.updateProductState,
      productJSON.updateProductState({ activate: !state }),
      {
        id: id,
      },
      "",
      "",
      "",
      MasterProduct,
      selectedWarehouse?.value
    );
    let response = await apiService.callAPI();
    if (response) {
      temp[productIndex].variants[variantIndex].variant.active = !state;
      
    }
    if (productState !== AllProduct) {
      temp.splice(productIndex, 1);
    }
    setProducts(temp);
  };
  const handleAvailableToSellToggle = async (
    variantId: string,
    currentState: boolean,
    productIndex: number,
    variantIndex: number
  ) => {
    if (!selectedWarehouse?.value) {
      return;
    }
    let temp = [...products];
    let apiService = new APICallService(
      product.availableToSell,
      productJSON.updateAvailableToSell({ availableToSell: !currentState }),
      {
        variantId: variantId,
        warehouseId: selectedWarehouse.value,
      },
      "",
      "",
      "",
      MasterProduct,
      selectedWarehouse.value
    );
    let response = await apiService.callAPI();
    if (response) {
      const inventory =
        temp[productIndex].variants[variantIndex].variant.inventoryInfo || [];
      const warehouseInvIndex = inventory.findIndex(
        (x: any) => x.warehouseId === selectedWarehouse.value
      );
      if (warehouseInvIndex !== -1) {
        inventory[warehouseInvIndex].availableToSell = !currentState;
      } else {
        inventory.push({
          warehouseId: selectedWarehouse.value,
          availableToSell: !currentState,
        });
      }
      temp[productIndex].variants[variantIndex].variant.inventoryInfo =
        inventory;
      setProducts(temp);
      success(response.message || "ATS status updated successfully");
    }
  };
  const handleCurrentPage = async (val: number) => {
    setPage(val);
    setKey(listMasterProducts.page, val);
    await fetchProducts(val, pageLimit, productState, categories, search);
  };
  const handleNextPage = async (val: number) => {
    setPage(val + 1);
    setKey(listMasterProducts.page, val + 1);
    await fetchProducts(val + 1, pageLimit, productState, categories, search);
  };
  const handlePreviousPage = async (val: number) => {
    setPage(val - 1);
    setKey(listMasterProducts.page, val - 1);
    await fetchProducts(val - 1, pageLimit, productState, categories, search);
  };
  const handlePageLimit = async (event: any) => {
    setPage(1);
    setPageLimit(event.target.value);
    setKey(listMasterProducts.page, 1);
    setKey(listMasterProducts.limit, parseInt(event.target.value));
    await fetchProducts(
      1,
      event.target.value,
      productState,
      categories,
      search
    );
  };
  const handleProductStateChange = async (event: any) => {
    setKey(listMasterProducts.page, 1);
    setPage(1);
    await fetchProducts(1, pageLimit, event.value, categories, search);
    setProductState(event.value);
  };
  const handleCategoryFilter = async (event: any) => {
    setCategories(event);
    setKey(listMasterProducts.page, 1);
    setPage(1);
    await fetchProducts(1, pageLimit, productState, event, search);
  };

  const debounce = useDebounce(fetchProducts, 500)
  const handleSearch = async (value: string) => {
    setSearch(value);
    setKey(listMasterProducts.page, 1);
    setPage(1);
    await debounce(1, pageLimit, productState, categories, value);
  };
  const openMenuOnClick = async (id: any) => {
    setId(id);
    setShow(true);
    // navigate('/master/edit-business-certificates', { state: data })
  };
  const onMenuClose = async () => {
    setId("");
    setShow(false);
  };
  const onMenuOpen = async (id: any) => {
    setId(id);
    setShow(true);
  };
  const handleOption = async (event: any, data: any, index: number) => {
    let currentScrollY = window.scrollY;
    setKey(listMasterProducts.scrollPosition, currentScrollY);
    setKey(listMasterProducts.search, search, false);
    setKey(listMasterProducts.filterProductState, productState, false);
    setKey(listMasterProducts.filterCategories, categories, false);
    if (event.value === 1) {
      navigate("/UProcure/products/edit-product", {
        state: {
          _id: data._id,
          isMaster: false,
          moduleName: MasterProduct,
        },
      });
    } else if (event.value === 2) {
      setDeleteProductModalOpen(true);
      setSelectedProduct(data);
    } else if (event.value === 0) {
    }
    else if (event.value === 3) {
      setSearchTag(true)
      setSelectedProduct(data);
    }
  };

  const handleVariantOption = async (
    event: any,
    productId: any,
    data: any,
    index: number
  ) => {
    let currentScrollY = window.scrollY;
    setKey(listMasterProducts.scrollPosition, currentScrollY);
    setKey(listMasterProducts.search, search, false);
    setKey(listMasterProducts.filterProductState, productState, false);
    setKey(listMasterProducts.filterCategories, categories, false);
    if (event.value === 1) {
      navigate("/UProcure/products/edit-product-variant", {
        state: {
          _id: data._id,
          isMaster: false,
          moduleName: MasterProduct,
        },
      });
    } else if (event.value === 2) {
      setDeleteVariantModalOpen(true);
      setSelectedVariant(data);
      setProductId(productId);
    } else if (event.value === 0) {
    }
  };

  const handleProductDelete = async () => {
    setDeleteLoader(true);
    let apiService = new APICallService(
      product.deleteProduct,
      selectedProduct?._id,
      "",
      "",
      "",
      true,
      MasterProduct,
      selectedWarehouse?.value
    );
    let response = await apiService.callAPI();
    if (response.error) {
      setError(response.error);
    } else {
      setPage(1);
      setKey(listMasterProducts.page, 1);
      fetchProducts(1, pageLimit, productState, categories, search);
      success(catalogue.deleted);
      setDeleteProductModalOpen(false);
    }
    setDeleteLoader(false);
    
  };

  const handleProductModalHide = () => {
    setDeleteProductModalOpen(false);
    setError("");
  };

  const handleVariantDelete = async () => {
    let variantId = selectedVariant?._id;
    setDeleteLoader(true);
    let apiService = new APICallService(
      product.deleteProductVariant,
      "",
      { productId, variantId },
      "",
      "",
      true,
      MasterProduct,
      selectedWarehouse?.value
    );
    let response = await apiService.callAPI();
    if (response.error) {
      setError(response.error);
    } else {
      setPage(1);
      setKey(listMasterProducts.page, 1);
      fetchProducts(1, pageLimit, productState, categories, search);
      success(masterToast.deleteVariant);
      setDeleteVariantModalOpen(false);
    }
    setDeleteLoader(false);
  };

  const handleVariantModalHide = () => {
    setDeleteVariantModalOpen(false);
    setError("");
  };

  return (
    <>
      {deleteProductModalOpen && (
        <DeleteModal
          show={deleteProductModalOpen}
          onHide={handleProductModalHide}
          service={selectedProduct}
          handleDelete={handleProductDelete}
          error={error}
          loading={deleteLoader}
          serviceName="product"
          message={`Are you sure you want to delete ${selectedProduct.title} product?`}
          warnMessage={`By continuing, the ${selectedProduct.title} product will
          be removed permanently`}
        />
      )}
      {deleteVariantModalOpen && (
        <DeleteModal
          show={deleteVariantModalOpen}
          onHide={handleVariantModalHide}
          service={selectedVariant}
          handleDelete={handleVariantDelete}
          error={error}
          loading={deleteLoader}
          serviceName="variant"
          message={`Are you sure you want to delete ${selectedVariant.title} variant?`}
          warnMessage={`By continuing, the ${selectedVariant.title} variant will
          be removed permanently`}
        />
      )}
      <DraftProducts
        show={showDraftProducts}
        onHide={() => setShowDraftProducts(false)}
        showRemoveDraftProduct={showRemoveDraftProduct}
        handleShowRemoveDraftProduct={(id: string, title: string) => {
          handleShowRemoveDraftProduct(id, title);
        }}
        deleteId={id}
        title={title}
        handleHideRemoveDraftProduct={handleHideRemoveDraftProduct}
        handleDraftCount={handleDraftCount}
        moduleName={MasterProduct}
      />
      {showSearchTag && (
        <EditSearchTags show={showSearchTag} onHide={() => setSearchTag(false)} data={selectedProduct} onClose={
          async () => {
            setSearchTag(false)
            await fetchProducts(page, pageLimit, productState, categories, search);
          }
        } />
      )}
      <div className="p-5">
        <Row className="align-items-center mb-7">
          <Col sm>
            <h1 className="fs-22 fw-bolder">Master products</h1>
          </Col>
          {fetchLoader ? (
            <></>
          ) : (
            <>
              {draftCount > 0 ? (
                <Col sm={"auto"}>
                  <Button
                    variant="white"
                    type="button"
                    className="btn-flush text-primary fs-16 fw-bold"
                    onClick={() => {
                      setShowDraftProducts(true);
                      let currentScrollY = window.scrollY;
                      setKey(listMasterProducts.scrollPosition, currentScrollY);
                      setKey(listMasterProducts.search, search, false);
                      setKey(
                        listMasterProducts.filterProductState,
                        productState,
                        false
                      );
                      setKey(
                        listMasterProducts.filterCategories,
                        categories,
                        false
                      );
                    }}
                  >
                    {draftCount} draft products
                  </Button>
                </Col>
              ) : (
                <></>
              )}
            </>
          )}
          {/* <Col sm={"auto"}>
            {Method.hasPermission(MasterProduct, Add, currentUser) ? (
              <Button
                className="min-h-50px btn-lg"
                variant="primary"
                type="button"
                onClick={() => {
                  let currentScrollY = window.scrollY;
                  setKey(listMasterProducts.scrollPosition, currentScrollY);
                  setKey(listMasterProducts.search, search, false);
                  setKey(
                    listMasterProducts.filterProductState,
                    productState,
                    false
                  );
                  setKey(
                    listMasterProducts.filterCategories,
                    categories,
                    false
                  );
                  navigate("/UProcure/products/add-new-product");
                }}
              >
                Add new product
              </Button>
            ) : (
              <></>
            )}
          </Col> */}
        </Row>
        <Card className="bg-light border mb-7">
          <Card.Body className="px-7">
            <Row className="align-items-center">
              <Col lg={4}>
                <div className="d-flex align-items-center position-relative me-4">
                  <KTSVG
                    path="/media/icons/duotune/general/gen021.svg"
                    className="svg-icon-3 position-absolute ms-3"
                  />
                  <input
                    type="text"
                    id="kt_filter_search"
                    className="form-control form-control-white form-control-lg min-h-lg-60px ps-10"
                    placeholder="Search by product name…"
                    disabled={fetchLoader}
                    onChange={(event: any) => {
                      handleSearch(event.target.value.trimStart());
                    }}
                    value={search}
                  />
                </div>
              </Col>
              <Col lg={8}>
                <Row className="align-items-center">
                  <Col md={8}>
                    <div className="d-flex justify-content-between align-items-center">
                      <label htmlFor="" className="fs-16 fw-500">
                        Filter products
                      </label>
                      <div className="min-w-lg-300px mw-lg-300px">
                        <CustomComponentSelect
                          // onChange={(options: any) => {
                          //   if (Array.isArray(options)) {
                          //   }
                          // }}
                          defaultValue={categories}
                          isLoading={fetchCategoryLoader}
                          isDisabled={loading || fetchLoader}
                          hideSelectedOptions={false}
                          options={filterProducts}
                          onMenuClose={() => {
                            onMenuCategoryClose();
                          }}
                          onMenuOpen={() => {
                            onMenuCategoryOpen();
                          }}
                          onMenuScrollToBottom={onMenuCategoryScrollToBottom}
                          onInputChange={(newValue: any, { action }: any) => {
                            if (action === "input-change") {
                              handleInputCategoryChange(newValue);
                            }
                          }}
                          onChange={(event: any) => {
                            handleCategoryFilter(event);
                          }}
                          isMulti={true}
                        />
                      </div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <CustomSelectWhite
                      // defaultValue={[
                      //   {
                      //     value: AllProduct,
                      //     name: "All products",
                      //     label: (
                      //       <>
                      //         <span className="fs-16 fw-600 text-black mb-0">
                      //           All products
                      //         </span>
                      //       </>
                      //     ),
                      //   },
                      // ]}
                      defaultValue={
                        productOptions.find(
                          (option) => option?.value === productState
                        ) || productOptions[0]
                      }
                      isDisabled={loading || fetchLoader}
                      options={productOptions}
                      onChange={(event: any) => {
                        handleProductStateChange(event);
                      }}
                      isMulti={false}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          </Card.Body>
        </Card>
        {fetchLoader ? (
          <div className="d-flex justify-content-center">
            <Loader loading={fetchLoader} />
          </div>
        ) : (
          <>
            <Card className="border border-r10px">
              <Card.Body className="p-0">
                <div className="table-responsive">
                  <table className="table table-rounded table-row-bordered align-middle gs-7 gy-4">
                    <thead>
                      <tr className="fw-bold fs-16 fw-600 text-dark border-bottom h-70px align-middle">
                        <th className="min-w-400px">Product name</th>
                        <th className="min-w-100px">Activate / Deactivate</th>
                        <th className="min-w-100px">Available To Sell</th>
                        <th className="min-w-225px text-end"></th>
                      </tr>
                    </thead>
                    <tbody>
                      <>
                        {loading ? (
                          <>
                            <tr>
                              <td colSpan={4}>
                                <div className="w-100 fs-15 fw-bold d-flex justify-content-center">
                                  <Loader loading={loading} />
                                </div>
                              </td>
                            </tr>
                          </>
                        ) : (
                          <>
                            {products.length ? (
                              <>
                                {products.map(
                                  (productVal: any, index: number) => {
                                    return (
                                      <>
                                        <tr>
                                          <td>
                                            <div className="d-flex align-items-center">
                                              <div className="symbol symbol-50px me-5">
                                                <ImageWithFallback
                                                  src={
                                                    productVal.variants[0]
                                                      .variant.media[0].url
                                                  }
                                                  className="object-fit-contain"
                                                  alt=""
                                                  noDefaultSize
                                                />
                                              </div>
                                              <div className="fs-15 fw-600">
                                                {productVal.title.replace(
                                                  /\s*\)\s*/g,
                                                  ")"
                                                )}
                                              </div>
                                            </div>
                                          </td>
                                          <td>
                                            {productVal.variants.length &&
                                              productVal.variants[0].variant[
                                              "variantType"
                                              ][0] !== undefined ? (
                                              <span className="fs-20 fw-600">
                                                -
                                              </span>
                                            ) : (
                                              <label className="form-check form-switch form-check-custom form-check-solid ">
                                                <input
                                                  className="form-check-input form-check-success w-50px h-30px"
                                                  type="checkbox"
                                                  name="notifications"
                                                  value={
                                                    productVal.variants[0]
                                                      .variant["_id"]
                                                  }
                                                  checked={
                                                    productVal.variants[0]
                                                      .variant.active
                                                  }
                                                  onChange={() => {
                                                    updateProductState(
                                                      productVal.variants[0]
                                                        .variant.active,
                                                      productVal.variants[0]
                                                        .variant["_id"],
                                                      true,
                                                      index,
                                                      0
                                                    );
                                                  }}
                                                />
                                              </label>
                                            )}
                                          </td>
                                          <td>
                                            {productVal.variants.length &&
                                              productVal.variants[0].variant[
                                              "variantType"
                                              ][0] !== undefined ? (
                                              <span className="fs-20 fw-600">
                                                -
                                              </span>
                                            ) : (
                                              <label className="form-check form-switch form-check-custom form-check-solid ">
                                                <input
                                                  className="form-check-input form-check-success w-50px h-30px"
                                                  type="checkbox"
                                                  name="availableToSell"
                                                  disabled={!selectedWarehouse?.value}
                                                  checked={
                                                    productVal.variants[0].variant.inventoryInfo?.find(
                                                      (x: any) =>
                                                        x.warehouseId ===
                                                        selectedWarehouse?.value
                                                    )?.availableToSell 
                                                  }
                                                  onChange={() => {
                                                    handleAvailableToSellToggle(
                                                      productVal.variants[0]
                                                        .variant["_id"],
                                                      productVal.variants[0].variant.inventoryInfo?.find(
                                                        (x: any) =>
                                                          x.warehouseId ===
                                                          selectedWarehouse?.value
                                                      )?.availableToSell ,
                                                      index,
                                                      0
                                                    );
                                                  }}
                                                />
                                              </label>
                                            )}
                                          </td>
                                          <td className="text-end">
                                            <div className="d-flex flex-nowrap justify-content-end align-items-center">
                                              {productVal.variants.length &&
                                                productVal.variants[0].variant[
                                                "variantType"
                                                ][0] != undefined ? (
                                                <span
                                                  className="fs-16 fw-600 text-gray text-hover-primary cursor-pointer me-3"
                                                  onClick={() => {
                                                    handleVariationShow(
                                                      products.variants,
                                                      index
                                                    );
                                                  }}
                                                >
                                                  {productVal.showVariant
                                                    ? "Hide"
                                                    : "View"}{" "}
                                                  {productVal.variants.length}{" "}
                                                  variations
                                                </span>
                                              ) : (
                                                <>
                                                  {Method.hasPermission(
                                                    MasterProduct,
                                                    View,
                                                    currentUser
                                                  ) ? (
                                                    <Button
                                                      variant="primary"
                                                      className="uw-btn uw-btn-primary btn-active-light-primary fs-14 fw-600 me-5"
                                                      onClick={() => {
                                                        let currentScrollY =
                                                          window.scrollY;
                                                        setKey(
                                                          listMasterProducts.scrollPosition,
                                                          currentScrollY
                                                        );
                                                        setKey(listMasterProducts.search, search, false);
                                                        setKey(listMasterProducts.filterProductState, productState, false);
                                                        setKey(listMasterProducts.filterCategories, categories, false);
                                                        navigate(
                                                          "/UProcure/products/master-products/view-product-details",
                                                          {
                                                            state:
                                                              productVal
                                                                .variants[0]
                                                                .variant["_id"],
                                                          }
                                                        );
                                                      }}
                                                    >
                                                      View details
                                                    </Button>
                                                  ) : (
                                                    <></>
                                                  )}
                                                </>
                                              )}
                                              {Method.hasPermission(
                                                MasterProduct,
                                                Edit,
                                                currentUser
                                              ) ||
                                                Method.hasPermission(
                                                  MasterProduct,
                                                  Delete,
                                                  currentUser
                                                ) ? (
                                                <CustomSelectTable
                                                  // backgroundColor="#e0e0df"
                                                  marginLeft={'-90px'}
                                                  placeholder={
                                                    <img
                                                      className="img-fluid"
                                                      width={18}
                                                      height={5}
                                                      src={ThreeDotMenu}
                                                      alt=""
                                                    />
                                                  }
                                                  backgroundColor="white"
                                                  default={defaultValue}
                                                  options={[
                                                    ...(Method.hasPermission(MasterProduct, Edit, currentUser)
                                                      ? [{
                                                        label: (
                                                          <button className="btn btn-link fs-14 fw-500 text-black ms-3 p-4">
                                                            Edit details
                                                          </button>
                                                        ),
                                                        value: 1,
                                                      }]
                                                      : []),
                                                    ...(Method.hasPermission(MasterProduct, Edit, currentUser)
                                                      ? [{
                                                        label: (
                                                          <button className="btn btn-link fs-14 fw-500 text-black ms-3 p-4">
                                                            Edit search Tag
                                                          </button>
                                                        ),
                                                        value: 3,
                                                      }]
                                                      : []),
                                                    ...(Method.hasPermission(MasterProduct, Delete, currentUser)
                                                      ? [{
                                                        label: (
                                                          <button className="btn btn-link fs-14 fw-500 text-danger ms-3 p-4">
                                                            Delete details
                                                          </button>
                                                        ),
                                                        value: 2,
                                                      }]
                                                      : []),
                                                  ]}
                                                  show={
                                                    show && productVal._id === id
                                                  }
                                                  onMenuClose={() => {
                                                    onMenuClose();
                                                  }}
                                                  openMenuOnClick={() => {
                                                    openMenuOnClick(
                                                      productVal._id
                                                    );
                                                  }}
                                                  onMenuOpen={() => {
                                                    onMenuOpen(productVal._id);
                                                  }}
                                                  onChange={(event: any) => {
                                                    handleOption(
                                                      event,
                                                      productVal,
                                                      index
                                                    );
                                                  }}
                                                  isOptionDisabled={(
                                                    option: any
                                                  ) => option.value === 4}
                                                />
                                              ) : (
                                                <></>
                                              )}
                                            </div>
                                          </td>
                                        </tr>
                                        {productVal.showVariant ? (
                                          productVal.variants.map(
                                            (
                                              variantVal: any,
                                              variantIndex: number
                                            ) => {
                                              return (
                                                <tr className="table-active">
                                                  <td>
                                                    <div className="d-flex align-items-center">
                                                      <div className="symbol symbol-50px border me-5">
                                                        <ImageWithFallback
                                                          src={
                                                            variantVal.variant
                                                              .media[0].url
                                                          }
                                                          className="object-fit-contain"
                                                          alt=""
                                                          noDefaultSize
                                                        />
                                                      </div>
                                                      <div className="fs-15 fw-600">
                                                        {variantVal.variant.title.replace(
                                                          /\s*\)\s*/g,
                                                          ")"
                                                        )}
                                                      </div>
                                                    </div>
                                                  </td>
                                                  <td>
                                                    <label className="form-check form-switch form-check-custom form-check-solid ">
                                                      <input
                                                        className="form-check-input form-check-success w-50px h-30px"
                                                        type="checkbox"
                                                        name="notifications"
                                                        value={
                                                          variantVal.variant._id
                                                        }
                                                        checked={
                                                          variantVal.variant
                                                            .active
                                                        }
                                                        onChange={() => {
                                                          updateProductState(
                                                            variantVal.variant
                                                              .active,
                                                            variantVal.variant
                                                              ._id,
                                                            false,
                                                            index,
                                                            variantIndex
                                                          );
                                                        }}
                                                      />
                                                    </label>
                                                  </td>
                                                  <td>
                                                    <label className="form-check form-switch form-check-custom form-check-solid ">
                                                      <input
                                                        className="form-check-input form-check-danger w-50px h-30px"
                                                        type="checkbox"
                                                        name="availableToSell"
                                                        disabled={!selectedWarehouse?.value}
                                                        checked={
                                                          variantVal.variant.inventoryInfo?.find(
                                                            (x: any) =>
                                                              x.warehouseId ===
                                                              selectedWarehouse?.value
                                                          )?.availableToSell || true
                                                        }
                                                        onChange={() => {
                                                          handleAvailableToSellToggle(
                                                            variantVal.variant
                                                              ._id,
                                                            variantVal.variant.inventoryInfo?.find(
                                                              (x: any) =>
                                                                x.warehouseId ===
                                                                selectedWarehouse?.value
                                                            )?.availableToSell ||
                                                            true,
                                                            index,
                                                            variantIndex
                                                          );
                                                        }}
                                                      />
                                                    </label>
                                                  </td>
                                                  <td className="text-end">
                                                    <div className="d-flex flex-nowrap justify-content-end align-items-center">
                                                      {/* <Link to="view-product-details"> */}
                                                      {Method.hasPermission(
                                                        MasterProduct,
                                                        View,
                                                        currentUser
                                                      ) ? (
                                                        <Button
                                                          variant="primary"
                                                          className="uw-btn uw-btn-primary me-3 btn-active-light-primary fs-14 fw-600"
                                                          onClick={() => {
                                                            let currentScrollY =
                                                              window.scrollY;
                                                            setKey(
                                                              listMasterProducts.scrollPosition,
                                                              currentScrollY
                                                            );
                                                            setKey(listMasterProducts.search, search, false);
                                                            setKey(listMasterProducts.filterProductState, productState, false);
                                                            setKey(listMasterProducts.filterCategories, categories, false);
                                                            navigate(
                                                              "/UProcure/products/master-products/view-product-details",
                                                              {
                                                                state:
                                                                  variantVal
                                                                    .variant._id,
                                                              }
                                                            );
                                                          }}
                                                        >
                                                          View details
                                                        </Button>
                                                      ) : (
                                                        <></>
                                                      )}
                                                      {/* </Link> */}
                                                      {/* <img
                                                          className="img-fluid"
                                                          width={18}
                                                          height={5}
                                                          src={
                                                              ThreeDotMenu
                                                          }
                                                          alt=""
                                                      /> */}
                                                      {Method.hasPermission(
                                                        MasterProduct,
                                                        Edit,
                                                        currentUser
                                                      ) ||
                                                        Method.hasPermission(
                                                          MasterProduct,
                                                          Delete,
                                                          currentUser
                                                        ) ? (
                                                        <CustomSelectTable
                                                          // backgroundColor="#e0e0df"
                                                          marginLeft={'-90px'}
                                                          placeholder={
                                                            <img
                                                              className="img-fluid"
                                                              width={18}
                                                              height={5}
                                                              src={ThreeDotMenu}
                                                              alt=""
                                                            />
                                                          }
                                                          default={defaultValue}
                                                          options={[
                                                            ...(Method.hasPermission(MasterProduct, Edit, currentUser)
                                                              ? [{
                                                                label: (
                                                                  <button className="btn btn-link fs-14 fw-500 text-black ms-3 p-4">
                                                                    Edit details
                                                                  </button>
                                                                ),
                                                                value: 1,
                                                              }]
                                                              : []),
                                                            ...(Method.hasPermission(MasterProduct, Delete, currentUser)
                                                              ? [{
                                                                label: (
                                                                  <button className="btn btn-link fs-14 fw-500 text-danger ms-3 p-4">
                                                                    Delete details
                                                                  </button>
                                                                ),
                                                                value: 2,
                                                              }]
                                                              : []),
                                                          ]}
                                                          show={
                                                            show &&
                                                            variantVal.variant
                                                              ._id === id
                                                          }
                                                          onMenuClose={() => {
                                                            onMenuClose();
                                                          }}
                                                          openMenuOnClick={() => {
                                                            openMenuOnClick(
                                                              variantVal.variant
                                                                ._id
                                                            );
                                                          }}
                                                          onMenuOpen={() => {
                                                            onMenuOpen(
                                                              variantVal.variant
                                                                ._id
                                                            );
                                                          }}
                                                          onChange={(
                                                            event: any
                                                          ) => {
                                                            handleVariantOption(
                                                              event,
                                                              productVal._id,
                                                              variantVal.variant,
                                                              index
                                                            );
                                                          }}
                                                          isOptionDisabled={(
                                                            option: any
                                                          ) => option.value === 3}
                                                        />
                                                      ) : (
                                                        <></>
                                                      )}
                                                    </div>
                                                  </td>
                                                </tr>
                                              );
                                            }
                                          )
                                        ) : (
                                          <></>
                                        )}
                                      </>
                                    );
                                  }
                                )}
                              </>
                            ) : (
                              <>
                                <tr>
                                  <td colSpan={4}>
                                    <div className="w-100 fs-15 fw-bold d-flex justify-content-center">
                                      No Data found
                                    </div>
                                  </td>
                                </tr>
                              </>
                            )}
                          </>
                        )}
                      </>
                    </tbody>
                  </table>
                </div>
              </Card.Body>
            </Card>
            {totalRecords > 0 ? (
              <Pagination
                totalRecords={totalRecords}
                currentPage={page}
                handleCurrentPage={handleCurrentPage}
                handleNextPage={handleNextPage}
                handlePreviousPage={handlePreviousPage}
                handlePageLimit={handlePageLimit}
                pageLimit={pageLimit}
              />
            ) : (
              <></>
            )}
          </>
        )}
      </div>
    </>
  );
};
export default MasterProducts;
